import { useEffect, useState, type JSX } from 'react';
import type { PropertyDescriptor } from '../../../core/definitions';
import { formatOffset, isoToLocalInput, localInputToIso } from './coerce';

type DateTimeDescriptor = Extract<PropertyDescriptor, { type: 'datetime' }>;

/**
 * A moment in time, stored as ISO-8601 with an explicit offset and edited as
 * wall-clock time in this browser's time zone. The stored offset is shown so
 * a value written elsewhere is not misread.
 */
export function DateTimeField(props: { desc: DateTimeDescriptor; value: string; id: string; onChange(value: string): void; onCommit(): void }): JSX.Element {
  const { desc, value, id, onChange, onCommit } = props;
  const [draft, setDraft] = useState<string | null>(null);
  const local = isoToLocalInput(value);
  useEffect(() => setDraft(null), [value]);
  const invalidStored = value !== '' && local === '';

  const apply = (text: string): void => {
    const iso = localInputToIso(text);
    if (iso && iso !== value) onChange(iso);
    setDraft(null);
  };

  const zone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return '';
    }
  })();
  const parsed = value === '' ? null : new Date(value);
  const storedOffset = /([+-]\d{2}:\d{2}|Z)$/.exec(value)?.[1];
  const localOffset = parsed && !Number.isNaN(parsed.getTime()) ? formatOffset(-parsed.getTimezoneOffset()) : null;

  return (
    <div className="ui-col" style={{ gap: 4 }}>
      <input
        id={id}
        className="ui-input"
        type="datetime-local"
        step={1}
        aria-label={desc.label}
        value={draft ?? local}
        onChange={(e) => {
          // The control reports a value only once every part is filled in; that is a complete edit.
          if (e.target.value) apply(e.target.value);
          else setDraft('');
        }}
        onBlur={onCommit}
      />
      <span className="ui-dim">
        {invalidStored
          ? `The stored value "${value}" is not a valid date and time.`
          : `Your time zone${zone ? ` (${zone})` : ''}${localOffset ? `, UTC${localOffset}` : ''}${
              storedOffset && localOffset && storedOffset !== localOffset && storedOffset !== 'Z' ? `; stored with UTC${storedOffset}` : ''
            }`}
      </span>
    </div>
  );
}
